import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import SideBar from "../../components/SideBar";
import DropdownMultiSelect from "../../components/DropdownMultiSelect";
import { ReportParamsContext } from "../../context/ReportParamsContext";
import { getEntities } from "../../API";

function normalizeList(response) {
  if (Array.isArray(response)) return response;
  if (response?._embedded) return Object.values(response._embedded).flat();
  if (response?.content) return response.content;
  return [];
}

function generateMonths(count) {
  const months = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString("en", { month: "long", year: "numeric" });
    months.push({ value, label });
  }
  return months;
}

const MONTH_OPTIONS = generateMonths(24);

function monthsToDateRange(months) {
  if (!months.length) return { fromDate: "", toDate: "" };
  const sorted = [...months].sort((a, b) => a.value.localeCompare(b.value));
  const firstVal = sorted[0].value;
  const lastVal  = sorted[sorted.length - 1].value;
  const [toYear, toMonth] = lastVal.split("-").map(Number);
  const lastDay = new Date(toYear, toMonth, 0).getDate();
  return {
    fromDate: `${firstVal}-01`,
    toDate:   `${lastVal}-${String(lastDay).padStart(2, "0")}`,
  };
}

function rangeLabel(months) {
  if (!months.length) return "No period selected";
  const sorted = [...months].sort((a, b) => a.value.localeCompare(b.value));
  if (sorted.length === 1) return sorted[0].label;
  return `${sorted[0].label} → ${sorted[sorted.length - 1].label}`;
}

const DEFAULT_MONTHS = MONTH_OPTIONS.slice(0, 3);

export default function PerformanceLayout({ sideBarItems }) {
  const location = useLocation();
  const isRepReport = location.pathname.includes("rep_report");

  const [selectedMonths,   setSelectedMonths]   = useState(DEFAULT_MONTHS);
  const [allReps,          setAllReps]          = useState([]);
  const [allManufacturers, setAllManufacturers] = useState([]);
  const [collapsed,        setCollapsed]        = useState(false);

  const [commonParams, setCommonParams] = useState(() => {
    const { fromDate, toDate } = monthsToDateRange(DEFAULT_MONTHS);
    return {
      fromDate,
      toDate,
      representatives: [],
      manufacturers: [],
      manufacturer: null,
      provinces: [],
      territories: [],
    };
  });

  useEffect(() => {
    getEntities("representatives", "?size=1000").then((data) => {
      setAllReps(normalizeList(data));
    });
    getEntities("manufacturers", "?size=1000").then((data) => {
      setAllManufacturers(normalizeList(data));
    });
  }, []);

  const handleMonthChange = (months) => {
    setSelectedMonths(months);
    const { fromDate, toDate } = monthsToDateRange(months);
    setCommonParams((prev) => ({ ...prev, fromDate, toDate }));
  };

  const handleRepChange = (reps) => {
    setCommonParams((prev) => ({ ...prev, representatives: reps }));
  };

  const handleMfrChange = (mfrs) => {
    setCommonParams((prev) => ({ ...prev, manufacturers: mfrs }));
  };

  return (
    <>
      <SideBar items={sideBarItems} />
      <ReportParamsContext.Provider value={[commonParams, setCommonParams]}>
        <div className="d-flex flex-column flex-grow-1 overflow-hidden">

          <div className="border-bottom bg-light">
            <div className="d-flex justify-content-between align-items-center px-4 pt-1 pb-1">
              <span className="text-muted small fst-italic">
                {rangeLabel(selectedMonths)}
              </span>
              <button
                className="btn btn-sm btn-link text-muted text-decoration-none p-0"
                onClick={() => setCollapsed((c) => !c)}
              >
                {collapsed ? "▼ Show parameters" : "▲ Hide parameters"}
              </button>
            </div>

            {!collapsed && (
              <div className="px-4 pb-3">
                <div className="row g-2 align-items-end">
                  <div className="col-md-4">
                    <label className="form-label small mb-1">Period (months)</label>
                    <DropdownMultiSelect
                      options={MONTH_OPTIONS}
                      value={selectedMonths}
                      onChange={handleMonthChange}
                      getOptionLabel={(o) => o.label}
                      getOptionValue={(o) => o.value}
                      placeholder="Select months…"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small mb-1">
                      Representatives
                      {isRepReport && (
                        <span className="text-muted ms-2" style={{ fontSize: "0.75rem" }}>
                          (selected in report)
                        </span>
                      )}
                    </label>
                    <DropdownMultiSelect
                      options={allReps}
                      value={commonParams.representatives}
                      onChange={handleRepChange}
                      getOptionLabel={(o) => `${o.firstName} ${o.lastName}`}
                      getOptionValue={(o) => o.id}
                      placeholder="All representatives…"
                      disabled={isRepReport}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small mb-1">Manufacturers</label>
                    <DropdownMultiSelect
                      options={allManufacturers}
                      value={commonParams.manufacturers}
                      onChange={handleMfrChange}
                      getOptionLabel={(o) => o.name}
                      getOptionValue={(o) => o.id}
                      placeholder="All manufacturers…"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <Outlet />
        </div>
      </ReportParamsContext.Provider>
    </>
  );
}
